import {matrixService} from '../../../services/matrixService';
import logger from '../../../services/logger';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {resolveMatrixUserIds} from '../../groupCalls/services/ringClient';
import type {RingMembersOutcome} from '../../groupCalls/services/groupCallOrchestrator';

export type AddPersonOutcome = RingMembersOutcome | 'unavailable';

const JOIN_WAIT_MS = 10_000;
const JOIN_POLL_MS = 250;

const membershipOf = (roomId: string, matrixUserId: string): string | undefined =>
    matrixService.getClient()?.getRoom(roomId)?.getMember(matrixUserId)?.membership;

export const isRoomMember = (roomId: string, matrixUserId: string): boolean => membershipOf(roomId, matrixUserId) === 'join';

export const namedRoomOf = (roomId: string): string | undefined => {
    const room = matrixService.getClient()?.getRoom(roomId);
    const name = room?.currentState.getStateEvents('m.room.name', '')?.getContent()?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : undefined;
};

const waitForJoin = async (roomId: string, matrixUserId: string): Promise<boolean> => {
    const deadline = Date.now() + JOIN_WAIT_MS;
    while (Date.now() < deadline) {
        if (isRoomMember(roomId, matrixUserId)) {
            return true;
        }
        await new Promise(resolve => window.setTimeout(resolve, JOIN_POLL_MS));
    }
    return isRoomMember(roomId, matrixUserId);
};

export const matrixIdForUser = async (userId: number): Promise<string | undefined> =>
    (await resolveMatrixUserIds([userId])).get(userId);

export const addPersonToCall = async (roomId: string, matrixUserId: string): Promise<AddPersonOutcome> => {
    try {
        if (!isRoomMember(roomId, matrixUserId)) {
            await matrixService.inviteUserToRoom(roomId, matrixUserId);
            if (!await waitForJoin(roomId, matrixUserId)) {
                return 'not_joined';
            }
        }
        return await groupCallService.ringMembers([matrixUserId]);
    } catch (error) {
        logger.warn('Group call add person failed', {
            source: 'group_call',
            event: 'add_person_failed',
            roomId,
            error: error instanceof Error ? error.message : String(error),
        });
        return 'unavailable';
    }
};
